
import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Project, Risk, Activity, Approval } from "@/types";
import { useMockProjects, useMockCreateProject } from "@/hooks/useMockProjects";
import { useMockCreateRisk, useMockUpdateRisk } from "@/hooks/useMockRisks";
import { useMockCreateActivity, useMockUpdateActivity } from "@/hooks/useMockActivities";
import { toast } from "@/hooks/use-toast";

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  isLoading: boolean;
  error: Error | null;
  createProject: (project: Omit<Project, 'id'>) => Promise<void>;
  addRisk: (projectId: string, risk: Omit<Risk, 'id'>) => Promise<void>;
  updateRisk: (projectId: string, riskId: string, updates: Partial<Risk>) => Promise<void>;
  updateActivity: (projectId: string, activityId: string, updates: Partial<Activity>) => Promise<void>;
  addApproval: (projectId: string, stageId: number, approval: Omit<Approval, 'id'>) => Promise<void>;
  updateStage: (projectId: string, stageId: number, updates: any) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);
  const { data: projects = [], isLoading, error } = useMockProjects();
  const createProjectMutation = useMockCreateProject();
  
  // Load saved project from localStorage on mount
  useEffect(() => {
    const savedProjectId = localStorage.getItem('stratafold_current_project');
    if (savedProjectId && projects.length > 0) {
      const savedProject = projects.find(p => p.id === savedProjectId);
      if (savedProject) {
        setCurrentProjectState(savedProject);
      } else {
        // Remove invalid project ID from localStorage
        localStorage.removeItem('stratafold_current_project');
      }
    }
  }, [projects]);

  // Custom setter that also saves to localStorage
  const setCurrentProject = (project: Project | null) => {
    setCurrentProjectState(project);
    if (project) {
      localStorage.setItem('stratafold_current_project', project.id);
    } else {
      localStorage.removeItem('stratafold_current_project');
    }
  };

  // Debug logging for projects
  console.log('ProjectProvider - projects loaded:', projects.length, 'isLoading:', isLoading);
  console.log('ProjectProvider - currentProject:', currentProject?.name || 'none');
  
  const createRiskMutation = useMockCreateRisk();
  const updateRiskMutation = useMockUpdateRisk();
  const createActivityMutation = useMockCreateActivity();
  const updateActivityMutation = useMockUpdateActivity();

  const createProject = async (projectData: Omit<Project, 'id'>) => {
    try {
      await createProjectMutation.mutateAsync(projectData);
      toast({
        title: "Success",
        description: "Project created successfully",
      });
    } catch (error) {
      console.error('Error creating project:', error);
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive",
      });
      throw error;
    }
  };

  const addRisk = async (projectId: string, riskData: Omit<Risk, 'id'>) => {
    try {
      await createRiskMutation.mutateAsync({
        ...riskData,
        projectId,
      });
      toast({
        title: "Success",
        description: "Risk added successfully",
      });
    } catch (error) {
      console.error('Error adding risk:', error);
      toast({
        title: "Error",
        description: "Failed to add risk",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateRisk = async (projectId: string, riskId: string, updates: Partial<Risk>) => {
    try {
      await updateRiskMutation.mutateAsync({
        id: riskId,
        projectId,
        ...updates,
      });
      toast({
        title: "Success",
        description: "Risk updated successfully",
      });
    } catch (error) {
      console.error('Error updating risk:', error);
      toast({
        title: "Error",
        description: "Failed to update risk",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateActivity = async (projectId: string, activityId: string, updates: Partial<Activity>) => {
    try {
      await updateActivityMutation.mutateAsync({
        id: activityId,
        projectId,
        ...updates,
      });
      toast({
        title: "Success",
        description: "Activity updated successfully",
      });
    } catch (error) {
      console.error('Error updating activity:', error);
      toast({
        title: "Error",
        description: "Failed to update activity",
        variant: "destructive",
      });
      throw error;
    }
  };

  const addApproval = async (projectId: string, stageId: number, approvalData: Omit<Approval, 'id'>) => {
    try {
      // Mock implementation - just simulate success
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: "Success",
        description: "Approval request added successfully",
      });
    } catch (error) {
      console.error('Error adding approval:', error);
      toast({
        title: "Error",
        description: "Failed to add approval request",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateStage = async (projectId: string, stageId: number, updates: any) => {
    try {
      // For now, just log the update since stages are managed differently
      console.log('Updating stage:', { projectId, stageId, updates });
      
      toast({
        title: "Success",
        description: "Stage updated successfully",
      });
    } catch (error) {
      console.error('Error updating stage:', error);
      toast({
        title: "Error",
        description: "Failed to update stage",
        variant: "destructive",
      });
      throw error;
    }
  };

  const value = {
    projects,
    currentProject,
    setCurrentProject,
    isLoading,
    error: error as Error | null,
    createProject,
    addRisk,
    updateRisk,
    updateActivity,
    addApproval,
    updateStage,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
};
